import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import {
  Lightbulb,
  Bell,
  BookOpen,
  Heart,
  Sparkles,
  CheckCircle,
  Clock,
  Calendar,
  PlayCircle,
  FileText,
  ExternalLink,
  ChevronRight,
  TrendingUp,
  Target,
  Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PageAbout } from "@/components/page-about";

interface EngagementContent {
  id: string;
  type: string;
  title: string;
  content: string;
  category: string;
  targetLanguage: string;
  scheduledFor?: string;
  priority: string;
  metadata?: Record<string, any>;
  createdAt: string;
}

interface EngagementDelivery {
  id: string;
  contentId: string;
  userId: string;
  channel: string;
  status: string;
  sentAt?: string;
  openedAt?: string;
  clickedAt?: string;
  content?: EngagementContent;
}

interface FinancialResource {
  id: string;
  title: string;
  description: string;
  category: string;
  resourceUrl?: string;
  eligibilityDetails?: string;
  applicationSteps?: string[];
  contactInfo?: string;
  isActive: boolean;
}

const contentTypeIcons: Record<string, any> = {
  health_tip: Lightbulb,
  reminder: Bell,
  education: BookOpen,
  motivation: Heart,
  announcement: Sparkles,
};

const contentTypeColors: Record<string, string> = {
  health_tip: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  reminder: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  education: "bg-green-500/10 text-green-600 border-green-500/20",
  motivation: "bg-pink-500/10 text-pink-600 border-pink-500/20",
  announcement: "bg-purple-500/10 text-purple-600 border-purple-500/20",
};

const resourceCategoryColors: Record<string, string> = {
  medication_assistance: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  financial_counseling: "bg-green-500/10 text-green-600 border-green-500/20",
  insurance_help: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  emergency_funds: "bg-red-500/10 text-red-600 border-red-500/20",
  support_programs: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
};

export default function EngagementPage() {
  usePageTitle("Community Engagement", "Track and manage community engagement metrics across the HealthWallet platform.");
  const { toast } = useToast();
  const { t } = useTranslation();

  const { data: content, isLoading: contentLoading } = useQuery<EngagementContent[]>({
    queryKey: ["/api/engagement-content"],
  });

  const { data: deliveries, isLoading: deliveriesLoading } = useQuery<EngagementDelivery[]>({
    queryKey: ["/api/engagement-deliveries"],
  });

  const { data: resources, isLoading: resourcesLoading } = useQuery<FinancialResource[]>({
    queryKey: ["/api/financial-resources"],
  });

  const markReadMutation = useMutation({
    mutationFn: async (deliveryId: string) => {
      const response = await apiRequest("POST", `/api/engagement-deliveries/${deliveryId}/read`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/engagement-deliveries"] });
    },
  });

  const unreadDeliveries = deliveries?.filter(d => !d.openedAt) || [];
  const readDeliveries = deliveries?.filter(d => d.openedAt) || [];
  
  const healthTips = content?.filter(c => c.type === "health_tip") || [];
  const educationContent = content?.filter(c => c.type === "education") || [];
  const reminders = content?.filter(c => c.type === "reminder") || [];

  const totalDeliveries = deliveries?.length || 0;
  const openedCount = deliveries?.filter(d => d.openedAt).length || 0;
  const clickedCount = deliveries?.filter(d => d.clickedAt).length || 0;
  const engagementRate = totalDeliveries > 0 ? Math.round((openedCount / totalDeliveries) * 100) : 0;

  if (contentLoading || deliveriesLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-6 md:grid-cols-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageAbout
        title="Health Engagement"
        description="Stay informed about your health with personalized tips, reminders, and educational content. Access financial assistance resources and manage your notification preferences."
        features={[
          "Receive personalized health tips and medication reminders",
          "Access educational content about medication management",
          "Discover financial assistance and support programs",
          "Manage notification preferences by channel and type",
          "Track your engagement history and reading progress"
        ]}
        tips={[
          "Mark important content as read to track your progress",
          "Check the resources tab for financial assistance options",
          "Enable notifications to never miss important reminders"
        ]}
      />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold" data-testid="text-page-title">Health Engagement</h1>
          <p className="text-muted-foreground mt-1">
            Stay informed with health tips, reminders, and educational resources
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Unread Messages</CardTitle>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{unreadDeliveries.length}</div>
            <p className="text-xs text-muted-foreground">New health updates</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Health Tips</CardTitle>
            <Lightbulb className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{healthTips.length}</div>
            <p className="text-xs text-muted-foreground">Available tips</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Engagement Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{engagementRate}%</div>
            <Progress value={engagementRate} className="h-2 mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Resources</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{resources?.length || 0}</div>
            <p className="text-xs text-muted-foreground">Financial resources</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="inbox">
        <TabsList>
          <TabsTrigger value="inbox" data-testid="tab-inbox">
            Inbox
            {unreadDeliveries.length > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 text-xs flex items-center justify-center">
                {unreadDeliveries.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="tips" data-testid="tab-tips">Health Tips</TabsTrigger>
          <TabsTrigger value="education" data-testid="tab-education">Education</TabsTrigger>
          <TabsTrigger value="resources" data-testid="tab-resources">Financial Resources</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Your Messages</CardTitle>
              <CardDescription>Health tips, reminders, and updates personalized for you</CardDescription>
            </CardHeader>
            <CardContent>
              {unreadDeliveries.length > 0 || readDeliveries.length > 0 ? (
                <div className="space-y-4">
                  {unreadDeliveries.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium flex items-center gap-2">
                        <Bell className="w-4 h-4" />
                        New
                      </h4>
                      {unreadDeliveries.map((delivery) => {
                        const Icon = contentTypeIcons[delivery.content?.type || "announcement"] || Sparkles;
                        const colorClass = contentTypeColors[delivery.content?.type || "announcement"];
                        return (
                          <div
                            key={delivery.id}
                            className="flex items-start gap-4 p-4 rounded-lg border bg-primary/5 border-primary/20"
                            data-testid={`delivery-unread-${delivery.id}`}
                          >
                            <div className={`p-2 rounded-full ${colorClass}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="font-medium">{delivery.content?.title}</h5>
                                <Badge variant="secondary" className="text-xs">New</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {delivery.content?.content}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {delivery.sentAt && format(new Date(delivery.sentAt), "MMM d, h:mm a")}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => markReadMutation.mutate(delivery.id)}
                              data-testid={`button-mark-read-${delivery.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {readDeliveries.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                        <CheckCircle className="w-4 h-4" />
                        Read
                      </h4>
                      {readDeliveries.slice(0, 5).map((delivery) => {
                        const Icon = contentTypeIcons[delivery.content?.type || "announcement"] || Sparkles;
                        return (
                          <div
                            key={delivery.id}
                            className="flex items-start gap-4 p-4 rounded-lg border"
                            data-testid={`delivery-read-${delivery.id}`}
                          >
                            <div className="p-2 rounded-full bg-muted">
                              <Icon className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="font-medium text-muted-foreground">{delivery.content?.title}</h5>
                              <p className="text-sm text-muted-foreground line-clamp-1">
                                {delivery.content?.content}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Opened {delivery.openedAt && format(new Date(delivery.openedAt), "MMM d")}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No messages yet</p>
                  <p className="text-sm">Health tips and reminders will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tips" className="space-y-4">
          {healthTips.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {healthTips.map((tip) => (
                <Card key={tip.id} data-testid={`tip-card-${tip.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={contentTypeColors.health_tip}>
                        <Lightbulb className="w-3 h-3 mr-1" />
                        Health Tip
                      </Badge>
                      <Badge variant="outline">{tip.category}</Badge>
                    </div>
                    <CardTitle className="text-base">{tip.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{tip.content}</p>
                  </CardContent>
                  <CardFooter className="text-xs text-muted-foreground">
                    <Calendar className="w-3 h-3 mr-1" />
                    {format(new Date(tip.createdAt), "MMM d, yyyy")}
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-12">
              <CardContent className="text-center">
                <Lightbulb className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No health tips available</p>
                <p className="text-sm text-muted-foreground">Check back soon for helpful tips!</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="education" className="space-y-4">
          {educationContent.length > 0 ? (
            <div className="space-y-4">
              {educationContent.map((edu) => (
                <Card key={edu.id} data-testid={`education-card-${edu.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={contentTypeColors.education}>
                            <BookOpen className="w-3 h-3 mr-1" />
                            Education
                          </Badge>
                          <Badge variant="outline">{edu.category}</Badge>
                        </div>
                        <CardTitle className="text-lg">{edu.title}</CardTitle>
                      </div>
                      <Button variant="outline" size="default" data-testid={`button-learn-${edu.id}`}>
                        Learn More
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{edu.content}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-12">
              <CardContent className="text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No educational content yet</p>
                <p className="text-sm text-muted-foreground">Educational resources will be added soon</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="resources" className="space-y-4">
          {resourcesLoading ? (
            <div className="grid gap-4 md:grid-cols-2">
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
          ) : resources && resources.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {resources.map((resource) => (
                <Card key={resource.id} data-testid={`resource-card-${resource.id}`}>
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={resourceCategoryColors[resource.category] || "bg-gray-500/10 text-gray-600 border-gray-500/20"}>
                        {resource.category.replace(/_/g, " ")}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{resource.title}</CardTitle>
                    <CardDescription>{resource.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {resource.eligibilityDetails && (
                      <div>
                        <h5 className="text-sm font-medium mb-1">Eligibility</h5>
                        <p className="text-sm text-muted-foreground">{resource.eligibilityDetails}</p>
                      </div>
                    )}
                    {resource.applicationSteps && resource.applicationSteps.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium mb-1">How to Apply</h5>
                        <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
                          {resource.applicationSteps.map((step, idx) => (
                            <li key={idx}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                    {resource.contactInfo && (
                      <div>
                        <h5 className="text-sm font-medium mb-1">Contact</h5>
                        <p className="text-sm text-muted-foreground">{resource.contactInfo}</p>
                      </div>
                    )}
                  </CardContent>
                  {resource.resourceUrl && (
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a href={resource.resourceUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Visit Resource
                        </a>
                      </Button>
                    </CardFooter>
                  )}
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-12">
              <CardContent className="text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No financial resources available</p>
                <p className="text-sm text-muted-foreground">Resources will be added as they become available</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Stay Healthy, Stay Engaged
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-blue-600" />
                Daily Health Tips
              </h4>
              <p className="text-sm text-muted-foreground">
                Receive personalized health tips based on your medication needs and health goals.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <Bell className="w-4 h-4 text-orange-600" />
                Smart Reminders
              </h4>
              <p className="text-sm text-muted-foreground">
                Never miss important medication schedules with timely reminders in your language.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-green-600" />
                Learn & Grow
              </h4>
              <p className="text-sm text-muted-foreground">
                Access educational content about managing your health and medications effectively.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
